<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="passthrough-by-stage"
    />
    <ChartContent
      height="293.5px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" title="Passthrough rate by stage" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { calculatePercent } from '@/components/insights/helpers';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What is our pass-through rate for each stage?';

const plugins = [ChartDataLabels];

const widgetId = 'passthrough-by-stage';

const {
  stageScreen,
  stageAssessment,
  stageOffer,
  stageHire,
} = useColors();

const noWidgetData = computed(() => {
  const { total = {} } = props.data || {};
  const totalCopy = { ...total };

  delete totalCopy?.review;

  return !totalCopy || Object.keys(totalCopy).every((key) => !totalCopy[key]);
});

const chartData = computed(() => {
  const { total } = props.data || {};
  const {
    review: totalReview,
    screen: totalScreen,
    assessment: totalAssessment,
    offer: totalOffer,
    hired: totalHired,
  } = total || {};

  return {
    labels: ['Screening', 'Assessment', 'Offered', 'Hired'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
          stageHire.value,
        ],
        borderColor: [
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
          stageHire.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          calculatePercent(totalScreen, totalReview),
          calculatePercent(totalAssessment, totalScreen),
          calculatePercent(totalOffer, totalAssessment),
          calculatePercent(totalHired, totalOffer),
        ],
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));
</script>
